import { useEffect } from 'react';
import { startKeyboardFollow } from '@/lib/keyboard-follow';
import { FIELD_MIN_SCROLL } from '@/lib/picker-keyboard-scroll';

/**
 * Brings whatever field the user just focused into view above the soft keyboard,
 * along with its label.
 *
 * The browser does something like this already — it scrolls the focused element
 * into view — but only the element, in one instant jump, and only far enough to
 * make it barely visible. That's the movement that reads as abrupt: it fires
 * before the keyboard has finished arriving, so it aims at a viewport that is
 * still changing, and it knows nothing about the `<Label>` above the input.
 *
 * This replaces it with the same follow loop the pickers use (VABS applies that
 * only to its pickers; extending it to every field is Atlas's own step). Every
 * frame it re-measures against `visualViewport` and closes a fraction of the gap,
 * so the motion tracks the keyboard as it slides instead of racing it.
 *
 * Mount ONCE, high in the tree. One capturing `focusin` listener on the document
 * covers the whole app including dialogs and portals, and the scroll container is
 * resolved per focus by walking up from the focused element — so a field in a
 * fixed dialog panel scrolls that panel, not the page behind it.
 *
 * Phone widths only. On a tablet or desktop the keyboard (if any) leaves enough
 * room that moving the page under the user is meddling, not help.
 */

/** Phone widths only — matches the gate on the footer collapse. */
const MOBILE_QUERY = '(max-width: 767.98px)';

/** `<input>` types that summon a keyboard. Buttons, checkboxes and the like take
 *  focus without one, and must not drag the viewport around. */
const TEXT_INPUT_TYPES = new Set([
  'text', 'search', 'email', 'url', 'tel', 'password', 'number',
  'date', 'datetime-local', 'month', 'time', 'week',
]);

/** Whether focusing this element opens the soft keyboard. */
function isTextEntry(el: Element | null): boolean {
  if (!el) return false;
  if (el instanceof HTMLTextAreaElement) return true;
  if (el instanceof HTMLElement && el.isContentEditable) return true;
  if (el instanceof HTMLInputElement) return TEXT_INPUT_TYPES.has(el.type);
  return false;
}

export function useFocusedFieldScroll(): void {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const mq = window.matchMedia?.(MOBILE_QUERY);

    let stopFollow: (() => void) | null = null;
    const cancel = (): void => {
      stopFollow?.();
      stopFollow = null;
    };

    const onFocusIn = (e: FocusEvent): void => {
      cancel(); // a previous field's loop must not fight this one
      if (mq && !mq.matches) return;
      const el = e.target;
      if (!(el instanceof HTMLElement) || !isTextEntry(el)) return;
      // A search box inside an open picker is already being followed by
      // usePickerKeyboardScroll, anchored to the trigger. Two loops scrolling the
      // same container would fight each other, and the picker's anchor is the
      // correct one — it accounts for the list hanging below.
      if (el.closest('[data-radix-popper-content-wrapper]')) return;
      // A wide dead zone: the browser's own scroll-into-view has usually already
      // parked this field, and closing our extra 20px of gap on top of that is a
      // second movement for one focus — which reads as a glitch.
      stopFollow = startKeyboardFollow({ anchor: el, deadZone: FIELD_MIN_SCROLL });
    };

    // Capturing, so it still fires for components that stop propagation.
    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('focusout', cancel, true);
    return () => {
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('focusout', cancel, true);
      cancel();
    };
  }, []);
}
